import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import TryAgainError from 'invite-sign/adapters/try-again-error';
import config from 'invite-sign/config/environment';
import type GroupInviteLink from 'invite-sign/models/group-invite-link';
import type Location from 'invite-sign/models/location';
import type FeatureFlagsService from 'invite-sign/services/feature-flags';
import type LocalizationService from 'invite-sign/services/localization';
import type SessionService from 'invite-sign/services/session';
import { type AvailableLocales } from 'invite-sign/utils/date-fns-i18n';

export type GroupInviteLinkRouteModel = {
  groupInviteLink?: GroupInviteLink;
  location?: Location;
};

export default class GroupInviteRoute extends Route {
  @service declare featureFlags: FeatureFlagsService;
  @service declare localization: LocalizationService;
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare store: Store;

  async model({ public_token: publicToken }: { public_token: string }): Promise<GroupInviteLinkRouteModel> {
    await this.session.authenticate(
      { grant_type: 'invite_link', invite_link_token: publicToken, scope: 'public' },
      config.guestInviteClientId,
    );

    if (!this.session.isAuthenticated) {
      const errorData = this.session.errorData;

      // The error data (in this case) is just a string returned from the API. Ignore the various errors.
      // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
      if (`${errorData}` === 'expired_token') {
        void this.router.replaceWith('expired');
      } else {
        void this.router.replaceWith('unauthorized');
      }

      return {};
    }

    const groupInviteLink = await this.store
      .queryRecord<GroupInviteLink>('group-invite-link', { id: 'current' })
      .catch((err: unknown) => {
        if (err instanceof TryAgainError) {
          void this.router.replaceWith('try-again');
          return;
        }
        // For other errors (like 404), return undefined so the null check below handles it
        return undefined;
      });

    if (!groupInviteLink) {
      void this.router.replaceWith('unauthorized');
      return {};
    }

    const model = {
      groupInviteLink,
      location: groupInviteLink.location,
    };

    if (!groupInviteLink.active || groupInviteLink.isExpired) {
      void this.router.replaceWith('group-invite.show.expired');
      return model;
    }

    if (groupInviteLink.isAtCapacity) {
      void this.router.replaceWith('group-invite.show.exceeded');
      return model;
    }

    return model;
  }

  async afterModel(model: GroupInviteLinkRouteModel): Promise<void> {
    const { lang } = this.paramsFor('application');
    const locale = lang || model.location?.locale || this.localization.languageCode || 'en';
    const loader = document.getElementById('initial-loader');

    if (loader) loader.style.display = 'none';

    try {
      await this.localization.loadLocaleTask.perform(locale as AvailableLocales);
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to load locale:', error);
    }

    // Load feature flags SDK
    const companyId = model.location?.companyId;
    if (companyId) {
      await this.featureFlags.createSdk(`${companyId}`);
    }
  }
}
